import { useEffect, useState } from 'react';
import compilationService from '../../../services/compilationService';
import trainingService from '../../../services/trainingService';
import { useUser } from '../../authentication';
import { getCourse } from '../api/getCourse';
import { getNextAction } from '../api/getNextAction';
import { getPathway } from '../api/getPathway';
import { getTrainingSearchResult } from '../api/getTrainingSearchResult';
import { Compilation } from '../models/compilation';
import { CourseResponse } from '../models/courseResponse';
import { NextAction } from '../models/nextAction';
import { PathwayResponse } from '../models/pathwayResponse';
import { Training } from '../models/training';
import { TrainingCourse } from '../models/trainingCourse';
import { TrainingPathway } from '../models/trainingPathway';

export function transformTraining(
  searchData: Training,
  trainingData: PathwayResponse | CourseResponse,
  type: 'course' | 'pathway'
): Training {
  const itemNumber = searchData.itemNumber || '';

  return type === 'course'
    ? ({
        ...searchData,
        ...trainingData,
        type: 'course',
        itemNumber,
        isIncludedWithGridSubscription: trainingService.isIncludedWithGridSubscription(itemNumber),
      } as TrainingCourse)
    : ({
        ...searchData,
        ...trainingData,
        type: 'pathway',
        itemNumber,
        isIncludedWithGridSubscription: trainingService.isIncludedWithGridSubscription(itemNumber),
      } as TrainingPathway);
}

export const useTraining = (trainingId: string, compilationId?: string) => {
  const { user } = useUser();
  const [data, setData] = useState<{
    training?: Training;
    isLoading: boolean;
    error?: boolean;
  }>({
    isLoading: true,
  });
  const [compilation, setCompilation] = useState<Compilation>();
  const [nextAction, setNextAction] = useState<NextAction>();

  useEffect(() => {
    getTrainingSearchResult(trainingId)
      .then(training => {
        if (training.trainingType === 'Tasklist') {
          getCourse(trainingId)
            .then(tasklist => {
              if (tasklist && Object.keys(tasklist).length !== 0) {
                setData({
                  ...data,
                  training: transformTraining(training, tasklist, 'course'),
                  isLoading: false,
                });
              } else {
                setData({ isLoading: false, error: true });
              }
            })
            .catch(() => {
              setData({ isLoading: false, error: true });
            });
        } else {
          getPathway(trainingId)
            .then(workflow => {
              if (workflow && Object.keys(workflow).length !== 0) {
                setData({
                  ...data,
                  training: transformTraining(training, workflow, 'pathway'),
                  isLoading: false,
                });
              } else {
                setData({ isLoading: false, error: true });
              }
            })
            .catch(() => {
              setData({ isLoading: false, error: true });
            });
        }

        if (user?.userId) {
          getNextAction(training.trainingType, trainingId).then(setNextAction);
        }
      })
      .catch(() => {
        setData({ isLoading: false, error: true });
      });
  }, [trainingId, user?.userId]);

  useEffect(() => {
    if (compilationId) {
      compilationService.getCompilation(compilationId).then(compilation => {
        setCompilation(compilation);
      });
    }
  }, [compilationId]);

  return {
    data,
    compilation,
    nextAction,
  };
};
